import { useMemo } from 'react'
import { DOCUMENT_TYPE } from '@/constant/E_CLAIM'
import type {
  DocumentAction,
  DocumentItem,
} from '@/components/feature/HealthClaimDetailPage/SupportingDocumentsForClaimTabDocument/types'

export const useSupportingDocumentsForClaim = () => {
  const textAlert =
    'เอกสารของผู้เอาประกันจะต้องเซ็น “สำเนาถูกต้องใช้สำหรับเรียกร้องค่าสินไหมทดแทนกับบริษัทประกันภัยไทยวิวัฒน์เท่านั้น”'

  const documentConfigs = useMemo(
    () => [
      {
        code: DOCUMENT_TYPE.ID_CARD,
        title: 'ภาพถ่ายหรือสำเนาบัตรประชาชน',
        signed: true,
        required: true,
      },
      {
        code: DOCUMENT_TYPE.BANK_ACCOUNT,
        title: 'สำเนาหน้าบัญชีธนาคาร',
        signed: true,
        required: true,
      },
      { code: DOCUMENT_TYPE.RECEIPT, title: 'ใบเสร็จ', signed: false, required: true },
      { code: DOCUMENT_TYPE.MED_CERT, title: 'ใบรับรองแพทย์', signed: false, required: true },
      { code: DOCUMENT_TYPE.OTHER, title: 'อื่นๆ', signed: false, required: false },
    ],
    [],
  )

  const initialState: DocumentItem[] = useMemo(
    () =>
      documentConfigs.map((config) => ({
        ...config,
        claimDocuments: [],
        uploadClaimDocuments: [],
      })),
    [documentConfigs],
  )

  const documentReducer = (state: DocumentItem[], action: DocumentAction): DocumentItem[] => {
    switch (action.type) {
      case 'INIT':
        return action.payload
      case 'SET_UPLOADING':
        return state.map((doc) =>
          doc.code === action.code
            ? {
                ...doc,
                uploadClaimDocuments: [...doc.uploadClaimDocuments, action.payload],
              }
            : doc,
        )
      case 'REMOVE_CLAIMED':
        return state.map((doc) => {
          return doc.code === action.code
            ? {
                ...doc,
                claimDocuments: doc.claimDocuments.filter((docItem) => docItem.id !== action.id),
              }
            : doc
        })
      case 'REMOVE_UPLOADED':
        return state.map((doc) => {
          return doc.code === action.code
            ? {
                ...doc,
                uploadClaimDocuments: doc.uploadClaimDocuments.filter(
                  (docItem) => docItem.id !== action.id,
                ),
              }
            : doc
        })
      case 'REMOVE_UPLOAD_ALL':
        return state.map((doc) => ({
          ...doc,
          uploadClaimDocuments: [],
        }))
      default:
        return state
    }
  }

  const getAttachmentUrls = (
    attachments: DocumentItem[],
  ): { url: string; id: string; code: string; status: string; uploadChannel: string }[] => {
    return attachments
      .map((group) => [
        ...group.claimDocuments
          .filter((item) => item.mimeType !== 'application/pdf')
          .map((item) => ({
            url: item.path,
            id: item.id,
            code: item.documentType.code,
            status: 'SUCCESS',
            uploadChannel: item.uploadChannel,
          })),
        ...group.uploadClaimDocuments
          .filter((item) => item.mimeType !== 'application/pdf')
          .map((item) => ({
            url: item.url,
            id: item.id,
            code: item.type,
            status: 'UPLOADED',
            uploadChannel: 'backoffice-super-app',
          })),
      ])
      .flat()
  }

  return {
    textAlert,
    getAttachmentUrls,
    initialState,
    documentConfigs,
    documentReducer,
  }
}

export default useSupportingDocumentsForClaim
